import React from 'react';

import type { ButtonProps } from '@wwe-portal/ui/components/button';
import { Button } from '@wwe-portal/ui/components/button';
import type { ClubCoinAmountProps } from '@wwe-portal/ui/features/club-store/components';
import {
  RosterStarColor,
  ClubCoinAmount,
} from '@wwe-portal/ui/features/club-store/components';
import { cn } from '@wwe-portal/ui/lib/lib';
import titleBgWebp from '@wwe-portal/ui/assets/images/roster-header.webp';

import { SelectableRosterStar } from './roster-star';
import {
  PlayerRosterForClubStore,
  RosterLevelType,
} from '@wwe-portal/entities/roster.type';
import { RosterTier } from './roster-card/roster-tier';

export interface UpdateRosterProps {
  rosterId: number;
  level: RosterLevelType | undefined;
  stars: number | undefined;
}

export interface RosterFuseUpProps {
  onSelectLevel?: (level: RosterLevelType) => void;
  onSelectStar?: (selectedStar: number) => void;
  roster: PlayerRosterForClubStore;

  selectedStar?: number;
  selectedLevel?: RosterLevelType;
  fuseUpPrice?: number;

  clubCoinProps: ClubCoinAmountProps;
  className?: string;
  disabled?: boolean;
  isUpgradeMode?: boolean;
  buttonProps: Partial<ButtonProps>;
}

export function RosterFuseUp({
  onSelectLevel,
  onSelectStar,

  roster,
  selectedStar,
  selectedLevel,
  className,
  clubCoinProps,
  isUpgradeMode = false,
  buttonProps,
}: Readonly<RosterFuseUpProps>) {
  const getVariant = (tier: RosterLevelType) => {
    if (tier === roster.level) return 'active';
    if (tier === selectedLevel) return 'selected';
    return 'default';
  };

  return (
    <div
      className={cn(
        'font-poppins width-full flex max-h-[calc(100dvh-88px)] flex-col gap-6 overflow-y-auto rounded-[10px] px-2 py-6',
        className
      )}
    >
      <div className="relative flex w-full flex-col gap-5">
        <div>
          <div
            className="text-text300 flex w-full justify-center bg-contain py-2 text-center text-base font-semibold"
            style={{ backgroundImage: `url(${titleBgWebp})` }}
          >
            <span className="text-yellow800">{roster.title}</span>
          </div>
          <div className="flex justify-center">
            <ClubCoinAmount {...clubCoinProps} />
          </div>
        </div>
        <div className="text-text400 line-clamp-3 px-8 text-center text-xs">
          {roster.description}
        </div>
        <div className="relative flex h-[308px] flex-col items-center pb-5">
          <div className="relative h-full w-fit">
            <img
              alt={roster.name}
              className="h-full w-full object-contain"
              src={roster.img}
            />
            {roster.tier && <RosterTier tier={roster.tier} size="long" />}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-8">
        {isUpgradeMode && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 px-4">
              <div className="text-text400 text-center text-sm font-semibold">
                Select Stars Range
              </div>
              <div className="flex justify-center">
                {roster.level && roster.star != null && (
                  <SelectableRosterStar
                    hollowStar={roster.hollowStar}
                    level={selectedLevel ?? roster.level}
                    maxRate={6}
                    onClick={onSelectStar}
                    rate={roster.star}
                    selected={selectedStar}
                  />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-4 px-4">
              <div className="text-text400 text-center text-sm font-semibold">
                Select Stars Color
              </div>
              <div className="flex justify-between gap-6">
                <div>
                  <RosterStarColor
                    onClick={onSelectLevel}
                    tier="B"
                    variant={getVariant('B')}
                  />
                </div>
                <div>
                  <RosterStarColor
                    onClick={onSelectLevel}
                    tier="S"
                    variant={getVariant('S')}
                  />
                </div>
                <div>
                  <RosterStarColor
                    onClick={onSelectLevel}
                    tier="G"
                    variant={getVariant('G')}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="px-2">
          <Button
            className={cn('w-full text-base', buttonProps.className)}
            size="large"
            variant="orange"
            {...buttonProps}
          >
            <span className="drop-shadow">{buttonProps.children}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
